/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   PageLTRDetail.tsx
 */

import { getRootRelativeUrlFromFiltersAndLocation } from '@zcode-ui/filters'
import { useMountAndUnmount } from '@zcode-ui/hook'
import { guessMapDimensions, selectMapDimensions } from '@zcode-ui/map'
import { fetchResidentQuotesForBuilding } from '@zcode-ui/web-zumper-features'
import { selectIsBot } from '@zcode-ui/web-zumper-global-state'
import { DETAIL_CALL } from '@zcode-ui/web-zumper-integrations'
import {
  DetailPageContext,
  type DetailPageContextType,
  PageLTRDetailRoot,
} from '@zcode-ui/web-zumper-pages'
import { NAV_SELECTIONS } from '@zcode-ui/zumper-header'
import type { AutocompleteResult } from '@zcode/bizos'
import { AnalyticsC as AnalyticsConsts, Origin, keepBetaParam } from '@zcode/nr-utils'
import { residentQuotes as residentQuotesApi } from '@zcode/nr/es/shared/api'
import {
  selectDisplayMedia,
  selectFocusedGalleryUrlFromLocation,
  selectGalleryUrlFromLocation,
  selectHasRoomTypeFloorplans,
} from '@zcode/nr/es/shared/bizos/rentable/selectors'
import { BREAKPOINT_1020 } from '@zcode/nr/es/shared/constants/grid'
import { Size, getImagePlaceholder as _getImagePlaceholder } from '@zcode/nr/es/shared/utils'
import type { FloorplanListing, MaybeEmptyProperty, Property } from '@zcode/state-type'
import type { TitleCase } from '@zcode/utils'
import { routesConstants } from '@zcode/zumper-routes'
import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import type { Dispatch } from 'redux'
import { trackAIAssistantEntryClick } from '../../components/AIAssistant/thunks/trackAIAssistantEntryClick'
import { trackAIAssistantEntryImpression } from '../../components/AIAssistant/thunks/trackAIAssistantEntryImpression'
import { AppBannerContainer } from '../../components/AppBanner/AppBannerContainer'
import { useLoadGoogleAds } from '../../components/GoogleAd/hooks'
import { ManageTitle } from '../../components/ManageTitle/ManageTitle'
import { useStreamChannel } from '../../components/MessageButton/useStreamChannel'
import { useAlertCreation } from '../../components/Notification/hooks/useAlertCreation'
import { AlertMeButton as AlertMeButtonNZR } from '../../components/SaveSearch/AlertMeButton'
import { InactiveSaveSearchSection as InactiveSaveSearchSectionNZR } from '../../components/SaveSearch/InactiveSaveSearchSection/InactiveSaveSearchSection'
import { NotifyMeButtonContainer as NotifyMeButton } from '../../components/SaveSearch/InactiveSaveSearchSection/NotifyMeButtonContainer'
import { DetailAds } from '../../components/detail/DetailAds/DetailAds'
import { GalleryViewDetailContainerLazyLoadableSection } from '../../components/detail/GalleryViewDetail/GalleryViewDetailContainerLazyLoadableSection'
import { TourBookingContainerNew as TourBookingContainer } from '../../components/detail/TourBookingNew/TourBookingContainerNew'
import { useViewCount } from '../../components/detail/helpers/viewCount/useViewCount'
import { useAIAssistant } from '../../components/detail/hooks/useAIAssistant'
import { useFullDetailTrack } from '../../components/detail/hooks/useDetailTrack'
import { default as useMobileView } from '../../components/detail/hooks/useMobileView'
import { useRemoveAppClipMeta } from '../../components/detail/hooks/useRemoveAppClipMeta'
import { default as useShowMessageModal } from '../../components/detail/hooks/useShowMessageModal'
import { useShowTourModal } from '../../components/detail/hooks/useShowTourModal'
import { FavoriteContainer as NzrFavoriteContainer } from '../../components/favorite/FavoriteContainer'
import { selectShouldLimitLeads } from '../../components/message/MessageForm/helpers/getShouldLimitLeads'
import { NavbarContainer as NavbarNZR } from '../../components/navbar/NavbarContainer'
import navigateToAutocompleteResult from '../../modules/autocomplete/thunks/navigateToAutocompleteResult'
import { openFloorplanModal } from '../../modules/detail/actions/index'
import { selectEntity } from '../../modules/detail/modules/entity/selectors/index'
import { selectIsFloorplanDetailModalOpen } from '../../modules/detail/selectors/index'
import { default as trackFloorplanView } from '../../modules/marketing/thunks/trackFloorplanView'
import { mixpanelCall } from '../../modules/mixpanel/thunks/mixpanelCall'
import { trackDetailEvent } from '../../modules/traktor/thunks/trackDetailEvent'
import { trackFloorplanEvent as _trackFloorplanEvent } from '../../modules/traktor/thunks/trackFloorplanEvent'
import { trackUnloadableTours } from '../../modules/traktor/thunks/trackUnloadableTour'
import { useDispatch } from '../../modules/useDispatch'
import { default as preload } from '../../routes/preload'
import { detailPageTitle } from '../../utils/page'
import { trackCallEvent } from '../../utils/trackCallEvent'
import { getUrlParams } from '../../utils/urls/getUrlParams'
import { BottomHalf } from './BottomHalf/BottomHalf'
import { DetailFlagButtonContainer } from './DetailFlagButtonContainer'
import { DetailMessageButton } from './DetailMessageButton'
import { DetailSaveSearchButton as SaveSearchButton } from './DetailSaveSearchButton'
import { DetailTourButton } from './DetailTourButton'
import { EngrainPropertyMap as EngrainPropertyMapNZR } from './EngrainPropertyMap'
import { FloorplanDetailModalLoadable } from './FloorplanDetail/FloorplanDetailModalLazyLoadable'
import { getHoodAndCityLinks } from './helpers/getHoodAndCityLinks'
import { mediaBarTracking } from './helpers/mediaBarTracking'
import { useMessaged } from './hooks/useMessaged'
import { useMixpanelThunks } from './hooks/useMixpanelThunks'
import { useViewDetailPageSection } from './hooks/useViewDetailPageSection'

const DetailAppBanner = () => <AppBannerContainer type="DETAIL" />

const FloorplanDetailModal = () => {
  const isOpen = useSelector(selectIsFloorplanDetailModalOpen)
  if (!isOpen) {
    return null
  }
  return <FloorplanDetailModalLoadable />
}

const FavoriteContainer = (props: Parameters<DetailPageContextType['FavoriteContainer']>[0]) => {
  return <NzrFavoriteContainer analyticChannel={AnalyticsConsts.FULL_DETAIL} {...props} />
}

const AlertMeButton = (props: Parameters<DetailPageContextType['AlertMeButton']>[0]) => {
  return <AlertMeButtonNZR {...props} />
}

const InactiveSaveSearchSection = (
  props: Parameters<DetailPageContextType['InactiveSaveSearchSection']>[0]
) => {
  return <InactiveSaveSearchSectionNZR {...props} />
}

const Navbar = ({ isBuilding, isStatic }: Parameters<DetailPageContextType['Navbar']>[0]) => {
  return (
    <NavbarNZR
      // The old FullDetailView.jsx takes a different approach: it calls
      // `getFiltersFromShortTermSearchObject(...)` which sets `filters` in redux
      // based on the query string
      initialLeaseLength={NAV_SELECTIONS.LONG_TERM}
      isStatic={isStatic}
      origin={isBuilding ? Origin.BUILDING : Origin.LISTING}
    />
  )
}

const EngrainPropertyMap = (props: Parameters<DetailPageContextType['EngrainPropertyMap']>[0]) => {
  return <EngrainPropertyMapNZR {...props} />
}

const getImagePlaceholder = (groupId: number) => _getImagePlaceholder(groupId, Size.small)

const _trackCall =
  ({ entity, origin }: { entity: Property | null | undefined; origin: Origin }) =>
  (dispatch: Dispatch) => {
    dispatch(trackDetailEvent({ additionalProps: { origin }, entity, name: DETAIL_CALL }))
    dispatch(mixpanelCall({ entity, origin }))
    trackCallEvent(entity ?? undefined)
  }

export const PageLTRDetail = React.memo(() => {
  const dispatch = useDispatch()
  const shouldLimitLeads = useSelector(selectShouldLimitLeads)
  const entity: MaybeEmptyProperty = useSelector(selectEntity) ?? {}
  const searchLinks = getHoodAndCityLinks(entity)
  const isBot = useSelector(selectIsBot)
  const showAIAssistant = !!useAIAssistant(entity)
  useRemoveAppClipMeta(entity)
  useMobileView(BREAKPOINT_1020)
  useFullDetailTrack(entity, false)
  useShowMessageModal({ entity })
  useShowTourModal({ entity })
  useViewCount(entity)
  useAlertCreation()
  useMountAndUnmount({
    onMount: () => {
      if (entity.isBuilding && !isBot) {
        void dispatch(
          fetchResidentQuotesForBuilding({ buildingId: entity.buildingId, residentQuotesApi })
        )
      }
      void GalleryViewDetailContainerLazyLoadableSection.load()
    },
  })

  const timeoutId = useRef<NodeJS.Timeout>()
  useEffect(() => {
    //if we're navigating back from the gallery page
    // reset sticky nav duration back to 0.25s
    if (typeof document !== 'undefined') {
      const transitionDuration = document.documentElement.style.getPropertyValue(
        '--sticky-nav-transition-duration'
      )
      if (transitionDuration === '0s') {
        timeoutId.current = setTimeout(() => {
          document.documentElement.style.removeProperty('--sticky-nav-transition-duration')
        }, 1500)
      }
    }
    return () => {
      clearTimeout(timeoutId.current)
    }
  }, [])
  const { isMessaged } = useMessaged()
  const mapDimensions = useSelector(selectMapDimensions)
  const MixpanelThunks = useMixpanelThunks()
  const openFloorplanDetailModal = useCallback(
    (payload: Parameters<typeof openFloorplanModal>[0]) => {
      dispatch(openFloorplanModal(payload))
      dispatch(trackFloorplanView(payload.selectedFloorplan))
    },
    [dispatch]
  )

  const handleBackBtnClick = useCallback(
    (result: AutocompleteResult) => {
      const { height, width } = guessMapDimensions(mapDimensions)
      void dispatch(navigateToAutocompleteResult({ result, height, width }))
    },
    [dispatch, mapDimensions]
  )

  const trackCall = useCallback(
    (props: Parameters<typeof _trackCall>[0]) => {
      dispatch(_trackCall(props))
    },
    [dispatch]
  )
  const traktorEvent = useCallback(
    (props: Parameters<typeof trackDetailEvent>[0]) => {
      dispatch(trackDetailEvent(props))
    },
    [dispatch]
  )
  const trackUnloadableTour = useCallback(
    (props: Parameters<typeof trackUnloadableTours>[0]) => {
      dispatch(trackUnloadableTours(props))
    },
    [dispatch]
  )

  const trackFloorplanEvent = useCallback(
    (
      action: 'Show Floorplan Summary',
      entity: Property | null | undefined,
      label:
        | TitleCase<Exclude<FloorplanListing['bedText'], ''> | 'room'>
        | Exclude<FloorplanListing['title'], undefined>
    ) => {
      const bedroom =
        label === 'Room'
          ? '-1'
          : label === 'Studio'
            ? '0'
            : (`${Number.parseInt(label, 10)}` as const)
      const floorplans = entity?.floorplansByStatus?.[bedroom]?.available ?? []
      dispatch(_trackFloorplanEvent({ action, floorplans, title: label }))
    },
    [dispatch]
  )
  useLoadGoogleAds()
  const context = useMemo(
    (): DetailPageContextType => ({
      AnalyticsConsts,
      AlertMeButton,
      DetailAds,
      DetailAppBanner,
      DetailFlagButtonContainer,
      EngrainPropertyMap,
      FavoriteContainer,
      getImagePlaceholder,
      getRootRelativeUrlFromFiltersAndLocation,
      handleBackBtnClick,
      InactiveSaveSearchSection,
      keepBetaParam,
      mediaBarTracking,
      MessageButton: shouldLimitLeads ? forwardRef(() => null) : DetailMessageButton,
      Navbar,
      NotifyMeButton,
      openFloorplanDetailModal,
      preloadRoutes: preload,
      Routes: routesConstants,
      SaveSearchButton,
      selectDisplayMedia,
      selectFocusedGalleryUrl: selectFocusedGalleryUrlFromLocation,
      selectGalleryUrl: selectGalleryUrlFromLocation,
      selectHasRoomTypeFloorplans,
      selectIsMessaged: isMessaged,
      shouldLimitLeads,
      showAIAssistant,
      TourBookingContainer,
      TourButton: shouldLimitLeads ? () => null : DetailTourButton,
      trackCall,
      trackFloorplanEvent,
      trackFloorplanView,
      trackAIAssistantEntryClick,
      trackAIAssistantEntryImpression,
      trackUnloadableTour,
      traktorEvent,
      useStreamChannel,
      useViewDetailPageSection,
      MixpanelThunks,
      urlParamListingId: getUrlParams().listingId,
      searchLinks,
    }),
    [
      openFloorplanDetailModal,
      MixpanelThunks,
      isMessaged,
      shouldLimitLeads,
      trackCall,
      trackFloorplanEvent,
      traktorEvent,
    ]
  )

  return (
    <DetailPageContext.Provider value={context}>
      <PageLTRDetailRoot>
        <ManageTitle
          makeTitle={detailPageTitle}
          titleProps={useMemo(
            () => ({ isBuilding: entity.isBuilding, pageTitle: entity.pageTitle }),
            [entity]
          )}
        />
        <BottomHalf />
        <FloorplanDetailModal />
      </PageLTRDetailRoot>
    </DetailPageContext.Provider>
  )
})
